/**
 * DraftCommentsPanel — the comments on the latest private draft, for the owner and the delegate while they edit it
 * (wireframe richEditor; Ownership design, part 3). "Comments on text that has since changed" sit right above dev's MDX
 * editor; each commented sentence's stacked comments and replies sit directly ABOVE that sentence in the editor, as
 * the wireframe's cards over their line. Whoever holds the JD resolves: a thread as a whole, a separate comment alone.
 * Lexical owns the editor's DOM, so nothing is inserted into it: the cards are rendered (a portal) into the editor's
 * scroll area, over a top margin set on the sentence's block (Lexical keeps an element's style and does not observe
 * attribute changes), and the commented sentences are highlighted with the CSS Custom Highlight API. A sentence whose
 * block can't be found (the editor not filled in yet, or hidden in Source / Diff view) is listed above the editor.
 *   comments     — the open comments ({ feedback_id, anchor_text, text, reply_to, author_name, created_at })
 *   markdown     — the text being edited (a sentence no longer in it moves to the list at the top)
 *   canResolve   — the viewer holds the JD
 *   busy, onResolve(feedbackId)
 *   anchorUpdatesRef — set to a function the page calls on Save as Draft: the comments whose line was edited (kept,
 *                   its text changed), each with the sentence it is on now ([{ feedback_id, anchor_text }]), so the
 *                   server moves them with it. A removed or emptied line's comments are not in it (they move to
 *                   "Comments on text that has since changed", as before)
 *   editorRootRef — the editor's scroll area (position: relative), for the cards and the highlight. The panel
 *                   watches it while there are commented sentences, so the cards follow the editor as it fills in,
 *                   changes, wraps or switches to Source / Diff view (the comments then move to the list above).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { LoadingOutlined } from '@ant-design/icons';
import { SbTooltip, TooltipTriggerStyled } from 'uiCore';
import {
  COMMENTS_PANEL_TITLE, ORPHANS_TITLE, TOOLTIP_RESOLVE_COMMENT, TOOLTIP_RESOLVE_THREAD, canonicalText,
  getAnchorUpdates, getAnchoredThreads, getOrphanThreads, quoteAnchor,
} from 'Utils/draftFeedback';
import {
  AnchoredCardsStyled, AnchoredCardsListStyled, AnchoredLayerStyled, AnchoredToggleRowStyled, CommentHighlightStyle,
  CommentsGroupStyled, CommentsPanelStyled, ResolveButtonStyled,
} from './DraftFeedback.styles';
// the same comment button and cards as the shared person's Draft Feedback view
import { CommentsToggle, ThreadCard, countThreads } from './CommentThreadParts';

export const HIGHLIGHT_NAME = 'jd-draft-comment';

const BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6';
const inCards = (el) => Boolean(el.closest('[data-comment-layer]'));
// the same for a mutated node (an element, or the text inside one)
const inCardsNode = (node) => {
  const el = node?.nodeType === 1 ? node : node?.parentElement;
  return Boolean(el) && inCards(el);
};
// The innermost editor blocks holding the text: a list item wrapping a nested list holds its items' text too, so the
// item itself, not its wrapper, is the match
const innermost = (matches) => matches.filter((el) => !matches.some((other) => other !== el && el.contains(other)));
const blocksWith = (root, key) => innermost([...root.querySelectorAll(BLOCKS)]
  .filter((el) => !inCards(el) && canonicalText(el.textContent).includes(key)));

// The editor's blocks (paragraphs, list items, headings) that hold a commented sentence, as ranges
export const commentedRanges = (root, anchors) => {
  if (!root || typeof document === 'undefined') return [];
  const keys = anchors.map(canonicalText).filter(Boolean);
  return [...new Set(keys.flatMap((key) => blocksWith(root, key)))].map((el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range;
  });
};

// The first (innermost) editor block holding the sentence that is laid out, outside the cards placed in the root.
// null when there is none, or when the rich text is hidden (MDXEditor's Source / Diff view sets display: none): the
// sentence's comments are then listed above the editor.
export const findCommentedBlock = (root, anchor) => {
  const key = canonicalText(anchor);
  if (!root || !key) return null;
  return blocksWith(root, key).find((el) => el.getClientRects().length > 0) || null;
};

// Space between a card and its sentence
const CARD_GAP = 6;
// The element the editor's text actually scrolls in (MDXEditor's content wrapper, inside the scroll area): the cards
// are placed in it, so they scroll with their line. Falls back to the given root.
const EDITOR_SCROLLER = '.mdxeditor-root-contenteditable';
export const cardsHost = (root) => root?.querySelector?.(EDITOR_SCROLLER) || root || null;
// The cards sit at the RIGHT of the sentence's block (the text tends to be on the left), this share of its width
// (never narrower than CARD_MIN_WIDTH, never wider than the block)
const CARD_WIDTH_SHARE = 0.6;
const CARD_MIN_WIDTH = 420;
export const cardPlace = (blockWidth) => {
  const width = Math.min(blockWidth, Math.max(CARD_MIN_WIDTH, blockWidth * CARD_WIDTH_SHARE));
  return { width, offset: blockWidth - width };
};
// Put a block's own top margin back (none: remove ours)
const restoreMargin = (block, own) => {
  if (own) block.style.setProperty('margin-top', own);
  else block.style.removeProperty('margin-top');
};
// The right end of a block's first line of text (its top-right corner), where the folded 💬 button sits: the block
// itself is as wide as the editor, its text usually is not. Falls back to the block's right edge.
export const textRightEdge = (block) => {
  const box = block.getBoundingClientRect();
  if (typeof document === 'undefined' || typeof document.createRange !== 'function') return box.right;
  const range = document.createRange();
  range.selectNodeContents(block);
  const rects = typeof range.getClientRects === 'function' ? [...range.getClientRects()].filter((r) => r.width > 0) : [];
  if (!rects.length) return box.right;
  const firstTop = Math.min(...rects.map((r) => r.top));
  return Math.max(...rects.filter((r) => Math.abs(r.top - firstTop) < 2).map((r) => r.right));
};
const samePlaces = (a, b) => {
  const keys = Object.keys(b);
  return keys.length === Object.keys(a).length && keys.every((k) => a[k]
    && ['top', 'left', 'width'].every((f) => Math.abs(a[k][f] - b[k][f]) < 0.5));
};

// The cards of each commented sentence, right above its block in the editor's scroll area (see the header). Each
// block gets a top margin the height of its cards; the cards are placed in that space. The panel re-renders this
// whenever the editor changes (its observers), so the places follow.
const AnchoredCards = ({ root, groups, renderThread, openKeys, onToggle }) => {
  const cardRefs = useRef(new Map());
  const spaced = useRef(new Map());   // block → its own margin-top, put back when it no longer has cards
  const [places, setPlaces] = useState({});

  // Every render: the editor's DOM changes outside React; setPlaces only when a place moved
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const blocks = new Set(groups.map((g) => g.block));
    spaced.current.forEach((own, block) => {
      if (!blocks.has(block)) {
        restoreMargin(block, own);
        spaced.current.delete(block);
      }
    });
    groups.forEach(({ key, block }) => {
      const card = cardRefs.current.get(key);
      if (!card) return;
      if (!spaced.current.has(block)) spaced.current.set(block, block.style.marginTop);
      const margin = `${card.offsetHeight + CARD_GAP * 2}px`;
      // the editor's block, not React's: its style is set directly
      if (block.style.marginTop !== margin) block.style.setProperty('margin-top', margin);
    });
    const rootBox = root.getBoundingClientRect();
    const next = {};
    groups.forEach(({ key, block }) => {
      const card = cardRefs.current.get(key);
      if (!card) return;
      const box = block.getBoundingClientRect();
      const textRight = Math.min(textRightEdge(block), box.right);
      const top = box.top - rootBox.top + root.scrollTop - card.offsetHeight - CARD_GAP;
      if (!openKeys.has(key)) {
        // folded: a zero-width slot at the text's top-right corner; the 💬 button ends exactly there
        next[key] = { top, left: textRight - rootBox.left + root.scrollLeft, width: 0 };
        return;
      }
      // open: the thin thread at the top right, right-aligned with the line's block (as before)
      const { width, offset } = cardPlace(box.width);
      next[key] = { top, left: box.left - rootBox.left + root.scrollLeft + offset, width };
    });
    if (!samePlaces(places, next)) setPlaces(next);
  });

  // Gone (editing ended): give the blocks their own margins back, in the same commit
  useLayoutEffect(() => () => {
    spaced.current.forEach((own, block) => restoreMargin(block, own));
    spaced.current.clear();
  }, []);

  return createPortal(
    <AnchoredLayerStyled data-comment-layer="">
      {groups.map(({ key, threads }) => {
        const place = places[key];
        const open = openKeys.has(key);
        const { count } = countThreads(threads);
        // folded: only the 💬 button at the line's top right; open: the button, then the thread under it
        return (
          <AnchoredCardsStyled
            key={key}
            $collapsed={!open}
            data-testid="anchored-comments"
            ref={(el) => { if (el) cardRefs.current.set(key, el); else cardRefs.current.delete(key); }}
            style={{
              top: place?.top ?? 0, left: place?.left ?? 0, width: place ? place.width : undefined,
              ...(open ? {} : { overflow: 'visible' }),
              visibility: place ? 'visible' : 'hidden',
            }}
          >
            {open ? (
              // open: the 💬 button sits on the first comment's line, next to Resolve (no row of its own)
              <AnchoredCardsListStyled>
                {threads.map((t, i) => renderThread(t, i === 0
                  ? <CommentsToggle count={count} open onToggle={() => onToggle(key)} /> : null))}
              </AnchoredCardsListStyled>
            ) : (
              <AnchoredToggleRowStyled>
                <CommentsToggle count={count} open={false} onToggle={() => onToggle(key)} />
              </AnchoredToggleRowStyled>
            )}
          </AnchoredCardsStyled>
        );
      })}
    </AnchoredLayerStyled>,
    root,
  );
};

AnchoredCards.propTypes = {
  root: PropTypes.object.isRequired,   // eslint-disable-line react/forbid-prop-types -- a DOM element
  groups: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, block: PropTypes.object,  // eslint-disable-line react/forbid-prop-types
    threads: PropTypes.arrayOf(PropTypes.object) })).isRequired,   // eslint-disable-line react/forbid-prop-types
  renderThread: PropTypes.func.isRequired,
  openKeys: PropTypes.instanceOf(Set).isRequired,
  onToggle: PropTypes.func.isRequired,
};

const DraftCommentsPanel = ({
  comments = [], markdown = '', canResolve = false, busy = false, resolvingId = null, onResolve = undefined,
  editorRootRef = null, anchorUpdatesRef = null,
}) => {
  const textOrphans = getOrphanThreads(comments, markdown);
  const anchored = getAnchoredThreads(comments, markdown);
  // the editor's scroll area, once it is rendered (the ref is set after this panel's first render)
  const [root, setRoot] = useState(null);
  // The line each commented sentence was last placed on: while the holder edits that line its text no longer matches
  // the sentence, but the comment stays on it. It moves to the top only once the line is gone (removed, or emptied)
  const placedOn = useRef(new Map());
  const blockFor = (anchor) => {
    const exact = findCommentedBlock(root, anchor);
    if (exact) {
      placedOn.current.set(anchor, exact);
      return exact;
    }
    const last = placedOn.current.get(anchor);
    if (last && root && root.contains(last) && (last.textContent || '').trim() && last.getClientRects().length > 0) {
      return last;
    }
    placedOn.current.delete(anchor);
    return null;
  };
  // The threads whose sentence's text changed but whose line is still there stay with that line (being edited)
  const edited = [];
  const orphans = [];
  textOrphans.forEach((t) => {
    const anchor = t.root.anchor_text;
    if (root && blockFor(anchor)) {
      const entry = edited.find((e) => e.anchor === anchor);
      if (entry) entry.threads.push(t);
      else edited.push({ anchor, threads: [t] });
    } else {
      orphans.push(t);
    }
  });
  const placeable = [...anchored, ...edited];
  // On Save as Draft: the lines being edited as they are at that moment (a line gone by then is left out)
  if (anchorUpdatesRef) {
    anchorUpdatesRef.current = () => getAnchorUpdates(textOrphans.reduce((lines, t) => {
      const anchor = t.root.anchor_text;
      const block = root ? blockFor(anchor) : null;
      if (!block) return lines;
      const line = lines.find((l) => l.anchor === anchor);
      if (line) line.threads.push(t);
      else lines.push({ anchor, lineText: block.textContent || '', threads: [t] });
      return lines;
    }, []));
  }
  const [, setTick] = useState(0);
  // The sentences whose comments are open (folded behind their 💬 button by default, as on the Draft Feedback side)
  const [openKeys, setOpenKeys] = useState(() => new Set());
  // Clicking the text of a line that has comments opens them (only opens; the 💬 button folds them again)
  const groupsRef = useRef([]);
  useEffect(() => {
    if (!root) return undefined;
    const openOnClick = (event) => {
      const hit = groupsRef.current.find((g) => g.block.contains(event.target));
      if (hit) setOpenKeys((prev) => (prev.has(hit.key) ? prev : new Set(prev).add(hit.key)));
    };
    root.addEventListener('click', openOnClick);
    return () => root.removeEventListener('click', openOnClick);
  }, [root]);
  const toggleOpen = (key) => setOpenKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const hasAnchored = anchored.length + textOrphans.length > 0;
  // Every render, after the whole commit (the editor's ref is attached after this panel's own effects): setRoot only
  // when the element changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const current = editorRootRef?.current || null;
    if (current !== root) setRoot(current);
  });
  // While there are commented sentences: look for their blocks again (and place their cards again) when the editor's
  // content or size changes — it fills in after mounting, it is typed in, it wraps — not for the cards' own changes
  useEffect(() => {
    if (!root || !hasAnchored) return undefined;
    const again = () => setTick((t) => t + 1);
    const mutations = typeof MutationObserver !== 'undefined' ? new MutationObserver((records) => {
      if (records.some((r) => !inCardsNode(r.target))) again();
    }) : null;
    mutations?.observe(root, { childList: true, subtree: true, characterData: true, attributes: true,
      attributeFilter: ['style', 'class'] });
    const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(again) : null;
    resize?.observe(root);
    return () => {
      mutations?.disconnect();
      resize?.disconnect();
    };
  }, [root, hasAnchored]);
  // each sentence's block in the editor (sentences sharing a block share its cards); the rest stay listed here
  const groups = [];
  const unplaced = [];
  placeable.forEach(({ anchor, threads }) => {
    const block = blockFor(anchor);
    if (!block) {
      unplaced.push({ anchor, threads });
      return;
    }
    const group = groups.find((g) => g.block === block);
    if (group) group.threads.push(...threads);
    else groups.push({ key: anchor, block, threads: [...threads] });
  });

  useEffect(() => {
    const highlights = typeof CSS !== 'undefined' ? CSS.highlights : undefined;
    if (!highlights || typeof Highlight === 'undefined') return undefined;   // an older browser: no highlight
    // the commented sentences, plus the lines being edited (their text changed, they keep their highlight)
    const ranges = [...commentedRanges(editorRootRef?.current, anchored.map((a) => a.anchor)),
      ...edited.map(({ anchor }) => placedOn.current.get(anchor)).filter(Boolean).map((block) => {
        const range = document.createRange();
        range.selectNodeContents(block);
        return range;
      })];
    highlights.set(HIGHLIGHT_NAME, new Highlight(...ranges));   // eslint-disable-line no-undef -- a browser global
    return () => highlights.delete(HIGHLIGHT_NAME);
  });

  if (!orphans.length && !placeable.length) return null;
  // one thread: the shared ThreadCard, with Resolve (the real button, with its spinner) on the first comment
  // extra: another action after Resolve (the 💬 button, on the first comment of an open line)
  const thread = ({ root: first, replies }, extra = null) => {
    const resolving = resolvingId === first.feedback_id;
    const resolve = canResolve && (
      <SbTooltip content={replies.length ? TOOLTIP_RESOLVE_THREAD : TOOLTIP_RESOLVE_COMMENT}>
        <TooltipTriggerStyled>
          <ResolveButtonStyled type="button" disabled={busy} aria-busy={resolving}
            onClick={() => onResolve(first.feedback_id)}>
            {resolving ? <LoadingOutlined spin data-testid="resolve-loading" /> : '✓'} Resolve
          </ResolveButtonStyled>
        </TooltipTriggerStyled>
      </SbTooltip>
    );
    return (
      <React.Fragment key={first.feedback_id}>
        <ThreadCard compact comment={first} actions={resolve || extra ? <>{resolve}{extra}</> : null}
          testId="editor-comment" />
        {replies.map((r) => <ThreadCard compact key={r.feedback_id} comment={r} isReply testId="editor-comment" />)}
      </React.Fragment>
    );
  };
  groupsRef.current = groups;
  const cards = root && groups.length > 0
    && <AnchoredCards root={cardsHost(root)} groups={groups} renderThread={thread} openKeys={openKeys} onToggle={toggleOpen} />;
  if (!orphans.length && !unplaced.length) return <><CommentHighlightStyle />{cards}</>;
  return (
    <CommentsPanelStyled data-testid="draft-comments-panel">
      <CommentHighlightStyle />
      {cards}
      {orphans.length > 0 && (
        <CommentsGroupStyled data-testid="orphan-comments">
          <b>{ORPHANS_TITLE}</b>
          {orphans.map((t) => (
            <React.Fragment key={t.root.feedback_id}>
              <q data-testid="orphan-anchor">{quoteAnchor(t.root.anchor_text)}</q>
              {thread(t)}
            </React.Fragment>
          ))}
        </CommentsGroupStyled>
      )}
      {unplaced.length > 0 && (
        <CommentsGroupStyled>
          <b>{COMMENTS_PANEL_TITLE}</b>
          {unplaced.map(({ anchor, threads }) => (
            <React.Fragment key={anchor}>
              <q>{anchor}</q>
              {threads.map((t) => thread(t))}
            </React.Fragment>
          ))}
        </CommentsGroupStyled>
      )}
    </CommentsPanelStyled>
  );
};

DraftCommentsPanel.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.object),
  markdown: PropTypes.string,
  canResolve: PropTypes.bool,
  busy: PropTypes.bool,
  resolvingId: PropTypes.string,   // the comment being resolved right now (its button shows a spinner)
  onResolve: PropTypes.func,
  editorRootRef: PropTypes.shape({ current: PropTypes.any }),
  anchorUpdatesRef: PropTypes.shape({ current: PropTypes.func }),
};

export default DraftCommentsPanel;
